import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ExternalLink, FileCheck2, FileDown, Loader2, Award, Eye } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { breadcrumbSchema } from "@/lib/schema";

export default function CertificatesPage() {
  const [certs, setCerts] = useState(null);
  const [preview, setPreview] = useState(null); // {src, title}

  useSeo({
    title: "Certificates & Downloads — Authorised Distributor & Product Brochures | SSK Enterprises",
    description:
      "SSK Enterprises Authorised Distributor Certificate from 3AK Chemie Pvt. Ltd., official 3AK Chemie certificate archive, and current product brochures — Dye Penetrant Testing, Magnetic Particle Inspection, Smart Choice and KLYDE ZINCKED catalogue.",
    path: "/certificates",
    jsonLd: [breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Certificates & Downloads", path: "/certificates" }])],
  });

  useEffect(() => {
    api.get("/certificates").then((r) => setCerts(r.data)).catch(() => setCerts([]));
  }, []);

  const authorisation = useMemo(
    () => (certs || []).filter((c) => c.category_slug === "authorisation"),
    [certs]
  );
  const brochures = useMemo(
    () => (certs || []).filter((c) => c.document_type === "Brochure"),
    [certs]
  );

  return (
    <>
      {/* HERO */}
      <section className="bg-[#0F2747] text-white border-b border-slate-800">
        <div className="container-x py-14 md:py-20">
          <div className="text-[11px] font-mono-tech tracking-[0.22em] uppercase text-slate-400 mb-3">Certificates &amp; Downloads</div>
          <h1 className="font-display text-4xl md:text-5xl font-black leading-tight max-w-3xl">Authorised Distributor Certificate &amp; Product Brochures</h1>
          <p className="text-slate-300 mt-4 max-w-2xl leading-relaxed">
            View the SSK Enterprises Authorised Distributor Certificate, browse the official 3AK Chemie certificate archive, and download product brochures for the ranges we supply.
          </p>
        </div>
      </section>

      {certs === null ? (
        <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
      ) : (
        <>
          {/* Section 1: Authorisation */}
          <section className="section" data-testid="section-authorisation">
            <div className="container-x">
              <div className="max-w-3xl">
                <div className="text-[11px] font-mono-tech tracking-[0.22em] uppercase text-[#C8102E] font-bold mb-3">01 · Authorisation</div>
                <h2 className="font-display text-3xl md:text-4xl font-black text-[#0F2747] leading-tight">Authorisation</h2>
              </div>
              <div className="mt-10 grid md:grid-cols-2 gap-6">
                {authorisation.map((c) => (
                  <AuthorisationCard key={c.id} cert={c} onPreview={() => c.image_url && setPreview({ src: c.image_url, title: c.title, download: c.file_url })} />
                ))}
              </div>
            </div>
          </section>

          {/* Section 2: Product Brochures */}
          {brochures.length > 0 && (
            <section className="section section-alt" data-testid="section-brochures">
              <div className="container-x">
                <div className="max-w-3xl">
                  <div className="text-[11px] font-mono-tech tracking-[0.22em] uppercase text-[#C8102E] font-bold mb-3">02 · Product Brochures</div>
                  <h2 className="font-display text-3xl md:text-4xl font-black text-[#0F2747] leading-tight">Product Brochures</h2>
                  <p className="text-slate-600 mt-4 leading-relaxed">Download the official product brochures for the ranges we supply.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
                  {brochures.map((b) => (
                    <BrochureCard key={b.id} brochure={b} onPreview={() => b.image_url && setPreview({ src: b.image_url, title: b.title, download: b.file_url })} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* Lightbox */}
      {preview && (
        <Lightbox src={preview.src} title={preview.title} download={preview.download} onClose={() => setPreview(null)} />
      )}
    </>
  );
}

function AuthorisationCard({ cert, onPreview }) {
  const isExternal = cert.document_type === "External Link" || (!cert.file_url && !cert.image_url);
  return (
    <div className="tech-card p-7 flex flex-col" data-testid={`auth-${cert.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 flex-shrink-0 bg-[#C8102E] text-white flex items-center justify-center rounded-sm">
          {isExternal ? <ExternalLink className="w-5 h-5" /> : <Award className="w-5 h-5" />}
        </div>
        <div>
          <div className="text-[10px] font-mono-tech tracking-[0.14em] uppercase text-slate-500">{cert.document_type}</div>
          <h3 className="font-display font-black text-lg text-[#0F2747] leading-tight mt-1">{cert.title}</h3>
          {cert.related_product && <div className="text-sm text-slate-500 mt-2">{cert.related_product}</div>}
        </div>
      </div>
      <div className="mt-6 flex gap-2">
        {isExternal ? (
          <a href={cert.external_url} target="_blank" rel="noreferrer" className="btn-accent flex-1 text-sm" data-testid={`auth-${cert.id}-visit`}>
            <ExternalLink className="w-4 h-4" /> Visit Official Website
          </a>
        ) : (
          <>
            <button onClick={onPreview} className="btn-accent flex-1 text-sm" data-testid={`auth-${cert.id}-view`}>
              <Eye className="w-4 h-4" /> View Certificate
            </button>
            {cert.file_url && (
              <a href={cert.file_url} download className="btn-outline text-sm" data-testid={`auth-${cert.id}-download`} aria-label="Download">
                <FileDown className="w-4 h-4" />
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function BrochureCard({ brochure, onPreview }) {
  return (
    <div className="tech-card flex flex-col overflow-hidden" data-testid={`brochure-${brochure.id}`}>
      <button
        type="button"
        onClick={onPreview}
        className="block w-full aspect-[3/4] bg-slate-100 border-b border-slate-200 relative group overflow-hidden"
        aria-label={`Preview ${brochure.title}`}
      >
        {brochure.image_url ? (
          <img src={brochure.image_url} alt={brochure.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300"><FileCheck2 className="w-14 h-14" /></div>
        )}
      </button>
      <div className="p-5 flex flex-col flex-grow">
        <h3 className="font-display font-bold text-[#0F2747] leading-snug">{brochure.title}</h3>
        <div className="text-xs text-slate-500 mt-1">{brochure.related_product}</div>
        <div className="mt-auto pt-5 flex gap-2">
          <button onClick={onPreview} className="btn-outline flex-1 text-xs" data-testid={`brochure-${brochure.id}-view`}>
            <Eye className="w-3.5 h-3.5" /> View PDF
          </button>
          {brochure.file_url && (
            <a href={brochure.file_url} download className="btn-primary flex-1 text-xs" data-testid={`brochure-${brochure.id}-download`}>
              <FileDown className="w-3.5 h-3.5" /> Download
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function Lightbox({ src, title, download, onClose }) {
  const [scale, setScale] = useState(1);
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4 md:p-8"
      role="dialog" aria-modal="true" aria-label={title}
      onClick={onClose}
      data-testid="cert-lightbox"
    >
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setScale((s) => Math.max(0.5, s - 0.2))} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Zoom out" data-testid="lb-zoom-out">−</button>
        <button onClick={() => setScale((s) => Math.min(3, s + 0.2))} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Zoom in" data-testid="lb-zoom-in">+</button>
        {download && (
          <a href={download} download className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Download" data-testid="lb-download"><FileDown className="w-4 h-4" /></a>
        )}
        <button onClick={onClose} className="w-10 h-10 bg-[#C8102E] hover:bg-[#8F0E15] text-white flex items-center justify-center" aria-label="Close" data-testid="lb-close">✕</button>
      </div>
      <div className="max-w-6xl w-full max-h-full overflow-auto" onClick={(e) => e.stopPropagation()}>
        <img
          src={src}
          alt={title}
          loading="lazy"
          decoding="async"
          style={{ transform: `scale(${scale})`, transformOrigin: "top center", transition: "transform 200ms ease" }}
          className="w-full h-auto shadow-2xl bg-white"
        />
      </div>
    </div>
  );
}
